import { TranslateFn } from '@core/hooks/useTranslate';
import { BellFilled, RightOutlined, CloseOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Badge, Empty } from 'antd';
import moment from 'moment';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import HeaderDropdown from '../HeaderDropdown';
import SellerServices from '@root/modules/Sellers/Dashboard/services/dashboard';
import wareHouseService from '@root/modules/Warehouse/Dasboard/services/warehouse';
import './index.scss';
import { Notification, NotificationLink, SELLER_NOTIFICATION, WAREHOUSE_NOTIFICATION } from './defined';
import InfiniteScroll from "react-infinite-scroll-component";

export type GlobalNotificationRightProps = {
  t: TranslateFn;
  appRole: any;
};

const NotificationHeader: React.FC<GlobalNotificationRightProps> = ({
  t,
  appRole
}) => {
  const [eventSource, setEventSource] = useState<EventSource>();
  const [notiUnread, setNotiUnread] = useState<number>(0);
  const [refetch, setRefetch] = useState(0);
  const [scrollConfig, setScrollConfig] = useState({
    hasMore: false,
    page: 1,
    limit: 5,
  });
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const getListNotifications = async () => {
    handleNotiUnread();
    let result;
    switch (appRole) {
      case 'seller-admin':
        result = await SellerServices.getNotifications({page: scrollConfig.page, limit: scrollConfig.limit});
        break;
      case 'warehouse-admin':
        result = await wareHouseService.getNotifications({page: scrollConfig.page, limit: scrollConfig.limit});
      break;
    }

    const data = result?.data?.list ?? [];
    let items = data;
    if (scrollConfig.page > 1) {
      items = [...notifications, ...data];
    }

    setScrollConfig({
      ...scrollConfig,
      hasMore: items.length < result?.data?.total ?? 0,
    });
    setNotifications(items);
  };

  const getNotiEventSource = () => {
    switch (appRole) {
      case 'seller-admin':
        setEventSource(SellerServices.getNotiEventSource());
        break;
      case 'warehouse-admin':
        setEventSource(wareHouseService.getNotiEventSource());
      break;
    }
  }

  const handleOnMessage = () => {
    if (eventSource) {
      eventSource.onmessage = (event) => {
        console.log(event);
        setNotiUnread(1)
      }
    }
  }

  const setRefetchNotiList = () => {
    setRefetch(refetch + 1);
  }

  const handleOnClickNoti = (noti: Notification) => {
    noti.isRead = true;
    handleNotiUnread(noti);
  }

  const handleNotiUnread = async (noti?: Notification) => {
    let service;
    switch (appRole) {
      case 'seller-admin':
        service = SellerServices;
        break;
      case 'warehouse-admin':
        service = wareHouseService;
      break;
    }
    if (noti) {
      await service?.readNotification(noti.id);
    }
    const coundUnread = await service?.isUnreadNotification();
    setNotiUnread(coundUnread?.data ?? 0)
  }

  //TODO
  const deleteNotification = async (id: number) => {
    switch (appRole) {
      case 'seller-admin':
        await SellerServices.deleteNotification(id);
        break;
      case 'warehouse-admin':
        await wareHouseService.deleteNotification(id);
        break;
      default:
        break;
    }

    getListNotifications();
  }
  const generalLink = (noti: Notification) => {
    try {
      switch (appRole) {
        case 'seller-admin':
          return NotificationLink.seller[noti.type as SELLER_NOTIFICATION](noti.itemId);
        case 'warehouse-admin':
          return NotificationLink.warehouse[noti.type as WAREHOUSE_NOTIFICATION](noti.itemId);
        default:
          return '/';
      } 
    } catch (_) {
      return '/';
    }
  };

  const handleLoadmore = async () => {
    if (!scrollConfig.hasMore) {
      return;
    }

    setScrollConfig({
      ...scrollConfig,
      page: scrollConfig.page + 1,
    });
    setRefetchNotiList();
  }

  useEffect(() => {
      getListNotifications();
  }, [refetch]);

  useEffect(() => {
    handleOnMessage();
    // handle close eventSource
    window.addEventListener('beforeunload', (event) => {
      if (eventSource && eventSource.readyState !== EventSource.CLOSED) {
        eventSource.close();
      }
    });
}, [eventSource]);

  useEffect(() => {
    getNotiEventSource();
  }, []);

  const notificationDropdown = (
    <div className='notification-container'>
      <InfiniteScroll 
        dataLength={notifications.length}
        height={ notifications.length > 3 ? 300 : undefined}
        next={handleLoadmore}
        hasMore={scrollConfig.hasMore}
        loader={<h4 className='notification-loading'>Loading...</h4>}
        endMessage={ !notifications.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{margin: '5px 0'}} imageStyle={{height: '30px'}} /> : null}
      >
        {
          notifications.map((item, index) => 
            (
              <div className='notification-item' key={index}>
                <Link onClick={() => handleOnClickNoti(item)} to={generalLink(item)} className="notification-link">
                  <div>
                    <div className={classnames('notification-title', !item.isRead ? 'notification-unread' : '')}
                         dangerouslySetInnerHTML={{__html: item.title}} />
                    <span className='notification-created-at'>
                    {moment(item.createdAt).format('YYYY.MM.DD HH:mm:ss')}
                  </span>
                  </div>
                </Link>
                <div className="right notification-link" onClick={() => deleteNotification(item.id)}>
                  <CloseOutlined />
                </div>
              </div>
            )
          )
        }
      </InfiniteScroll>
    </div>
  );

  return (
    <>
      { ['seller-admin', 'warehouse-admin'].includes(appRole)
        ? 
        <>
          <HeaderDropdown
            className="right"
            trigger={['click']}
            overlay={notificationDropdown}
            onVisibleChange={(visible) => {
              if (visible) {
                setScrollConfig({
                  ...scrollConfig,
                  page: 1,
                });
                setRefetchNotiList();
              } else {
                // scroll to top when close
                setTimeout(() => {
                  document.getElementsByClassName('infinite-scroll-component')[0].scroll(0, 0);
                }, 200)
              }
            }}
          >
            <div style={{alignItems: 'center'}}>
              <Badge count={notiUnread} dot={true} offset={[-6, 6]}>
                <BellFilled className='notification-bell'/>
              </Badge>
            </div>
          </HeaderDropdown>
        </>
        : null 
      }
    </>
  );
};
export default NotificationHeader;
